// Auth API Client

import type {
  IUser,
  ILoginRequest,
  ILoginResponse,
  IChangePasswordRequest,
  IRefreshResponse,
} from '../types/auth';
import { httpClient } from './client';
import { assertObject, assertString, assertBoolean } from '../types/validation';
import { validateUser } from './users';

function validateLoginResponse(data: unknown, path: string): ILoginResponse {
  const obj = assertObject(data, path);
  return {
    user: validateUser(obj['user'], `${path}.user`),
    accessToken: assertString(obj['accessToken'], `${path}.accessToken`),
    refreshToken: assertString(obj['refreshToken'], `${path}.refreshToken`),
    mustChangePassword: assertBoolean(obj['mustChangePassword'], `${path}.mustChangePassword`),
  };
}

export const authApi = {
  /**
   * Login with identifier (login or email) and password
   */
  async login(request: ILoginRequest): Promise<ILoginResponse> {
    const { data } = await httpClient.post<unknown>('/auth/login', request);
    return validateLoginResponse(data, 'login');
  },

  /**
   * Change password
   */
  async changePassword(request: IChangePasswordRequest): Promise<ILoginResponse> {
    const { data } = await httpClient.post<unknown>('/auth/change-password', request);
    return validateLoginResponse(data, 'changePassword');
  },

  /**
   * Refresh access token
   */
  async refresh(refreshToken: string): Promise<IRefreshResponse> {
    const { data } = await httpClient.post<unknown>('/auth/refresh', {
      refreshToken,
    });
    const obj = assertObject(data, 'refresh');
    return {
      accessToken: assertString(obj['accessToken'], 'refresh.accessToken'),
      refreshToken: assertString(obj['refreshToken'], 'refresh.refreshToken'),
    };
  },

  /**
   * Logout (invalidate refresh token)
   */
  async logout(refreshToken: string): Promise<void> {
    await httpClient.post('/auth/logout', { refreshToken });
  },

  /**
   * Logout from all sessions
   */
  async logoutAll(): Promise<void> {
    await httpClient.post('/auth/logout-all');
  },

  /**
   * Get current user info
   */
  async getCurrentUser(): Promise<IUser> {
    const { data } = await httpClient.get<unknown>('/auth/me');
    return validateUser(data, 'getCurrentUser');
  },
};

